import React from 'react';
import { Weight, Target, Ruler, Activity, AlertCircle, Heart, User } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета ИМТ
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Определение цвета для ИМТ на основе категории
  const getBmiColor = (category) => {
    switch (category) {
      case 'Выраженный дефицит массы тела':
        return 'blue';
      case 'Недостаточная масса тела':
        return 'blue';
      case 'Нормальная масса тела':
        return 'green';
      case 'Избыточная масса тела':
        return 'amber';
      case 'Ожирение I степени':
        return 'orange';
      case 'Ожирение II степени':
        return 'red';
      case 'Ожирение III степени':
        return 'red';
      default:
        return 'gray';
    }
  };

  const bmiColor = getBmiColor(results.category);

  return (
    <ToolResult title="Результаты расчета индекса массы тела">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Индекс массы тела */}
        <div className={`rounded-lg bg-gradient-to-br from-${bmiColor}-50 to-${bmiColor}-100 dark:from-${bmiColor}-900/30 dark:to-${bmiColor}-800/30 p-5 border border-${bmiColor}-200 dark:border-${bmiColor}-800/50 shadow-sm`}>
          <h3 className={`text-base font-medium text-${bmiColor}-700 dark:text-${bmiColor}-300 mb-1.5 flex items-center gap-1.5`}>
            <Activity className="w-4 h-4"/>
            Индекс массы тела (ИМТ)
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-3xl font-bold text-${bmiColor}-800 dark:text-${bmiColor}-200`} aria-live="polite">
              {formatNumber(results.bmi, 1)}
            </span>
            <span className={`text-sm font-medium text-${bmiColor}-600 dark:text-${bmiColor}-400`}>
              кг/м²
            </span>
          </div>
          <p className={`text-xs text-${bmiColor}-600 dark:text-${bmiColor}-400 mt-1 font-medium`}>
            {results.category}
          </p>
        </div>

        {/* Идеальный вес */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <Target className="w-4 h-4"/>
            Рекомендуемый вес
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {formatNumber(results.idealWeight, 1)}
            </span>
            <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
              кг
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            Диапазон здорового веса: {formatNumber(results.idealWeightRange.min, 1)} — {formatNumber(results.idealWeightRange.max, 1)} кг
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Текущий вес" 
          value={`${formatNumber(results.weight, 1)} кг`} 
          color="emerald" 
          icon={<Weight className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Рост" 
          value={`${formatNumber(results.height, 1)} см`} 
          color="emerald" 
          icon={<Ruler className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Отклонение от рекомендуемого веса" 
          value={`${results.weightDifference > 0 ? '+' : ''}${formatNumber(results.weightDifference, 1)} кг (${results.weightDifferencePercent > 0 ? '+' : ''}${formatNumber(results.weightDifferencePercent, 1)}%)`} 
          color={results.weightDifference === 0 ? "green" : Math.abs(results.weightDifference) < 5 ? "amber" : "orange"}
        />
        
        <ResultItem 
          label="Суточная норма калорий" 
          value={`${results.dailyCalories.base} ккал`} 
          color="indigo"
          icon={<Activity className="w-4 h-4"/>} 
        />
        
        {results.dailyCalories.base !== results.dailyCalories.goal && (
          <ResultItem 
            label="Рекомендуемая норма калорий (с учетом цели)" 
            value={`${results.dailyCalories.goal} ккал`} 
            color="purple"
            icon={<Target className="w-4 h-4"/>} 
          />
        )}
        
        <ResultItem 
          label="Категория ИМТ" 
          value={results.category} 
          color={bmiColor}
        />
        
        <ResultItem 
          label="Описание" 
          value={results.description} 
          color="gray" 
        />

        {/* Информация о рекомендациях */}
        <div className="bg-blue-50 dark:bg-blue-900/50 rounded-lg p-4 mt-4 border border-blue-200 dark:border-blue-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <Heart className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Рекомендации для вашего ИМТ</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {results.recommendation}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Дополнительная информация о методике расчета */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <AlertCircle className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">О методике расчета</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Индекс массы тела рассчитывается по формуле ИМТ = вес(кг) / рост²(м²). 
              Нормы разработаны Всемирной Организацией Здравоохранения и рекомендованы Минздравом РФ. 
              Помните, что ИМТ – это ориентировочный показатель, который не учитывает состав тела, 
              возрастные особенности и индивидуальные физиологические параметры.
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;